import { settingsService } from './api';

// Same logic as api.js — resolves correct backend URL
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:3055/api'
    : 'https://area51crm.inplsoftwares.com/api');

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Fetch CRM booking financials via backend proxy.
 * Backend reads invoicingApiEndpoint + invoicingApiKey from app_settings.
 * No CORS issues for any role.
 */
async function fetchCrmBookingFinancials(fromDate, toDate, crmId = null) {
  const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
  if (crmId) params.append('crm_id', crmId);

  const response = await fetch(
    `${API_BASE_URL}/bookings/crm-financials?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  const data = await response.json();
  if (!response.ok || data.status === false) {
    console.warn('[Banquet API] Failed to fetch CRM booking financials:', data.message || 'External system error');
    // Return empty financials gracefully to prevent dashboard crash
    return { data: [] }; 
  }
  return data;
}

// The BMS financials endpoint filters on order_date — the date the sale was
// made. There is no event-date parameter, so anything keyed on when the
// function actually happens (function_date) has to be grouped client-side out
// of the whole booking book. A since-2020 window returns exactly that: every
// booking ever placed, which by definition contains every future event ever
// sold.
//
// One response, one cache, shared by every caller — the dashboard's event-axis
// money cards and the Finance event-date list have to agree row for row, and
// clicking from one to the other must not re-download several MB.
const ALL_ROWS_FROM = '2020-01-01';
let allRowsCache = null;
let allRowsInFlight = null;
// Bumped by every force, so a reply still in flight from before the Sync knows
// it has been superseded and does not clobber the fresher rows on its way in.
let allRowsGeneration = 0;

const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Every banquet booking on record, for grouping by event date client-side.
 * Cached for the session; pass force to bust it (the Finance Sync button).
 */
function fetchAllBookingRows({ force = false } = {}) {
  if (force) {
    allRowsCache = null;
    allRowsInFlight = null;
    allRowsGeneration += 1;
  }
  if (allRowsCache) return Promise.resolve(allRowsCache);
  if (allRowsInFlight) return allRowsInFlight;

  const generation = allRowsGeneration;
  const settle = () => { if (generation === allRowsGeneration) allRowsInFlight = null; };

  allRowsInFlight = fetchCrmBookingFinancials(ALL_ROWS_FROM, todayStamp())
    .then((res) => {
      const rows = res?.data || [];
      if (generation === allRowsGeneration) allRowsCache = rows;
      settle();
      return rows;
    })
    .catch((err) => { settle(); throw err; });

  return allRowsInFlight;
}

/** Cached rows if they are already in hand, else null — for lazy initial state. */
function peekAllBookingRows() {
  return allRowsCache;
}

export const bookingService = {
  fetchCrmBookingFinancials,
  fetchAllBookingRows,
  peekAllBookingRows,

  /**
   * Submit booking to external BMS via backend proxy.
   * Backend reads invoicingApiEndpoint + invoicingApiKey from app_settings.
   * No CORS issues for any role.
   */
  async submitToExternal(payload) {
    const response = await fetch(`${API_BASE_URL}/bookings/submit`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(data.message || 'Failed to submit booking to banquet system');
    return data;
  },

  /**
   * Submit tentative booking to external BMS via backend proxy.
   */
  async submitTentativeToExternal(payload) {
    const response = await fetch(`${API_BASE_URL}/bookings/submit-tentative`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(data.message || 'Failed to submit tentative booking to banquet system');
    return data;
  },

  /**
   * Fetch booking list via backend proxy.
   * Backend reads invoicingApiEndpoint + invoicingApiKey from app_settings.
   * No CORS issues for any role.
   *
   * @param {number} year
   * @param {number} month - 0-indexed (JS Date style)
   */
  async fetchBookingList(year, month) {
    const fromDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const toDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    const response = await fetch(
      `${API_BASE_URL}/bookings/banquet-list?from_date=${fromDate}&to_date=${toDate}`,
      { headers: getAuthHeaders() }
    );

    const data = await response.json();
    if (!data.status) {
      console.warn('[Banquet API] Failed to fetch banquet bookings:', data.message);
      return [];
    }

    // Normalize to CalendarView-compatible shape
    return (data.data || []).map((b) => ({
      id: `banquet-${b.id}`,
      banquetId: b.id,
      source: 'banquet',
      clientName: b.customer_name,
      eventDate: b.function_date,
      shift: b.booking_timing_name?.toLowerCase() === 'morning' ? 'Day' : 'Night',
      venue: b.lawn_name,
      functionName: b.function_name,
      noOfGuests: b.no_of_guest,
      orderNo: b.order_no,
      crmId: b.crm_id,
      mobileOne: b.mobile_one,
      mobileTwo: b.mobile_two,
      otherInstruction: b.other_instruction,
      shiftRemarks: b.shift_booking_remarks,
      setupName: b.setup_name,
      caterers: b.caterers_name,
    }));
  },

  /**
   * Fetch upcoming banquet bookings
   */
  async fetchUpcomingBookings(limit = 5) {
    const today = new Date();
    const fromDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Fetch for next 6 months to ensure we get upcoming ones
    const future = new Date();
    future.setMonth(future.getMonth() + 6);
    const toDate = `${future.getFullYear()}-${String(future.getMonth() + 1).padStart(2, '0')}-${String(future.getDate()).padStart(2, '0')}`;

    const response = await fetch(
      `${API_BASE_URL}/bookings/banquet-list?from_date=${fromDate}&to_date=${toDate}`,
      { headers: getAuthHeaders() }
    );

    const data = await response.json();
    if (!data.status) {
      console.warn('[Banquet API] Failed to fetch upcoming bookings:', data.message);
      return []; // Return empty gracefully instead of throwing, so dashboard doesn't show errors
    }

    // Normalize, sort and limit
    const bookings = (data.data || []).map((b) => ({
      id: `banquet-${b.id}`,
      banquetId: b.id,
      source: 'banquet',
      clientName: b.customer_name,
      title: b.function_name || 'Event',
      startTime: b.function_date,
      eventDate: b.function_date,
      shift: b.booking_timing_name?.toLowerCase() === 'morning' ? 'Day' : 'Night',
      venue: b.lawn_name,
      functionName: b.function_name,
      noOfGuests: b.no_of_guest,
      orderNo: b.order_no,
      crmId: b.crm_id,
      mobileOne: b.mobile_one,
      mobileTwo: b.mobile_two,
      otherInstruction: b.other_instruction,
      shiftRemarks: b.shift_booking_remarks,
      setupName: b.setup_name,
      caterers: b.caterers_name,
    })).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    return bookings.slice(0, limit);
  }
};
